
import { UNDO_STACK_KEY_NONE } from './UndoRedoManager';

import type { IUndoRedoManager } from './undoRedoTypes';
import type { UniversalAccessor } from '@/editor/core/universalAccessor';
import type * as ENGINE from '@gnsx/genesys.js';

import { applyActorSnapshot, takeActorSnapshot } from '@/editor/core/actions/objectActions';
import { markPrefabDirty } from '@/editor/core/actions/prefabActions';
import { normaliseScenePathForComparison } from '@/editor/core/actions/sceneRenameUtils';
import { normalizePrefabAssetPath } from '@/editor/core/prefabActionHelpers';
import { editorStore } from '@/editor/store';

// === Stack key helpers ===

/**
 * Returns the normalised stack key for a scene path, or the sentinel key when no scene is loaded.
 */
export function resolveSceneUndoStackKey(scenePath?: string | null): string {
  const path = scenePath ?? editorStore.project.currentScene;
  const normalised = normaliseScenePathForComparison(path);
  return normalised ? `scene:${normalised}` : UNDO_STACK_KEY_NONE;
}

/**
 * Returns the normalised stack key for a prefab isolation path.
 */
export function resolvePrefabUndoStackKey(prefabPath: string): string {
  return `prefab:${normalizePrefabAssetPath(prefabPath)}`;
}

/**
 * Returns the stack key that should be active given the current editor context
 * (prefab isolation mode takes precedence over the loaded scene).
 */
export function resolveActiveUndoStackKey(): string {
  const prefabEditPath = editorStore.scene.prefabEditPath;
  if (prefabEditPath) {
    return resolvePrefabUndoStackKey(prefabEditPath);
  }
  return resolveSceneUndoStackKey();
}

/**
 * Synchronises `undoRedoManager.activeStackKey` with the current editor context
 * (loaded scene or prefab isolation path). Call this whenever the editor context
 * changes — scene load, scene reset, open/close prefab.
 */
export function syncActiveUndoStack(): void {
  const key = resolveActiveUndoStackKey();
  editorStore.undoRedoManager.setActiveStack(key);
}

// === Routing helpers ===

/**
 * Returns the undo/redo manager for the currently loaded level scene, regardless
 * of whether prefab isolation mode is active. Use this for operations that are
 * inherently level-only (asset CRUD, outliner folder moves, paste actors, etc.)
 * and must always record to the scene stack.
 */
export function getSceneUndoRedo(): IUndoRedoManager {
  return editorStore.undoRedoManager.stackFor(resolveSceneUndoStackKey());
}

/**
 * Whether undo/redo should be recorded for the current prefab editor session.
 * Engine prefabs are view-only in prefab mode (no isolated undo stack mutations).
 */
export function canRecordPrefabEditorUndo(): boolean {
  if (!editorStore.isPrefabMode) {
    return true;
  }
  return !editorStore.scene.isEngineAsset;
}

const afterSnapshotMutation = (): void => {
  editorStore.scene.updateInspector();
  if (editorStore.isPrefabMode && !editorStore.undoRedoManager.isReplayingHistory()) {
    markPrefabDirty();
  }
};

/**
 * Records an undo step that restores the owning actor from a snapshot taken before `doFn` runs.
 * Uses the active undo stack (level vs prefab isolation). Skips history for read-only engine prefabs.
 */
export function recordActorSnapshotAction(
  actionName: string,
  parentAccessor: UniversalAccessor,
  doFn: () => void,
): void {
  if (!canRecordPrefabEditorUndo()) {
    doFn();
    afterSnapshotMutation();
    return;
  }

  const snapshot = takeActorSnapshot(parentAccessor);
  editorStore.undoRedoManager.recordAction(
    actionName,
    () => {
      doFn();
      afterSnapshotMutation();
    },
    () => {
      applyActorSnapshot(parentAccessor, snapshot);
      afterSnapshotMutation();
    },
  );
}

/**
 * Resolves an actor for undo/redo replay — world actors first, then prefab template selection.
 */
export function resolveActorForEditorUndo(actorUuid: string): ENGINE.Actor | null {
  const world = editorStore.scene.world;
  if (world) {
    const fromWorld = world.getActorByUuid(actorUuid);
    if (fromWorld) {
      return fromWorld;
    }
  }
  for (const selected of editorStore.scene.selectedObjects) {
    const actor = selected.asActor;
    if (actor?.uuid === actorUuid) {
      return actor;
    }
  }
  return null;
}
